import {
  Account,
  AccountDetail,
  BankAccount,
  contestentDetails,
  CountryData,
  PaginatedResponse,
  RegisterContestentPayload,
  RegisterUserPayload,
  UpiAccount,
} from '@/types';
import { apiSlice } from './apiSlice';
import { RegisterInvestorResponse } from './authApi';
import { PaymentRequestResponse } from './paymentRequestApi';

export const accountsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAccounts: builder.query<
      PaginatedResponse<Account>,
      {
        skip?: number;
        take?: number;
        search?: string;
        type?: string;
      }
    >({
      query: ({ skip = 0, take = 10, search, type }) => ({
        url: '/users',
        params: {
          skip,
          take,
          ...(search ? { search } : {}),
          onlyType: type == 'member' ? 'Member' : 'Tribe',
        },
      }),
      providesTags: ['Accounts'],
    }),

    getContestant: builder.query<
      PaginatedResponse<Account>,
      {
        skip?: number;
        take?: number;
        search?: string;
      }
    >({
      query: ({ skip = 0, take = 10, search }) => ({
        url: '/contestants',
        params: {
          skip,
          take,
          ...(search ? { search } : {}),
        },
      }),
      providesTags: ['Accounts'],
    }),
    getAccountById: builder.query<AccountDetail, number>({
      query: (accountId) => `/users/${accountId}`,
      providesTags: ['Accounts'],
    }),

    getContestentById: builder.query<contestentDetails, number>({
      query: (accountId) => `/contestants/${accountId}`,
      providesTags: ['Accounts'],
    }),

    getCountries: builder.query<CountryData, void>({
      query: () => ({
        url: '/countries',
        method: 'GET',
      }),
    }),

    registerUser: builder.mutation<RegisterInvestorResponse, RegisterUserPayload>({
      query: (payload) => ({
        url: '/accounts/create',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['Accounts'],
    }),

    registerContestent: builder.mutation<RegisterInvestorResponse, RegisterContestentPayload>({
      query: (payload) => ({
        url: '/contestants',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['Accounts'],
    }),

    getUserBankAccounts: builder.query<BankAccount[], number>({
      query: (accountId) => ({
        url: `/accounts/${accountId}/bank-accounts`,
        method: 'GET',
      }),
      providesTags: ['Accounts'],
    }),
    getAccountStatement: builder.query<PaymentRequestResponse<any>, any>({
      query: (params) => ({
        url: '/payment-request',
        method: 'GET',
        params,
      }),
      providesTags: ['Accounts'],
    }),
    getUserUpiAccounts: builder.query<UpiAccount[], number>({
      query: (accountId) => ({
        url: `/accounts/${accountId}/upi-accounts`,
        method: 'GET',
      }),
      providesTags: ['Accounts'],
    }),
    updateUserStatus: builder.mutation<
      void,
      {
        accountId: number;
        status?: 'Active' | 'Blocked';
      }
    >({
      query: ({ accountId, status }) => ({
        url: `/users/${accountId}/${status}`,
        method: 'POST',
        // body: {
        //   userId: accountId,
        //   status: status,
        // },
      }),
      invalidatesTags: ['Accounts'],
    }),
    updateContestentStatus: builder.mutation<
      void,
      {
        accountId: number;
        status?: 'Active' | 'Eliminated';
        // isDeleted?: boolean;
      }
    >({
      query: ({ accountId, status }) => ({
        url: `/contestants/${accountId}/${status}`,
        method: 'POST',
      }),
      invalidatesTags: ['Accounts'],
    }),

    updateContestentTeam: builder.mutation<
      void,
      {
        userId: number;
        teamId: 1 | 2 | 3;
      }
    >({
      query: ({ userId, teamId }) => ({
        url: `/contestants/${userId}`,
        method: 'PATCH',
        body: {
          teamId,
        },
      }),
      invalidatesTags: ['Accounts'],
    }),
    updateContestentAssignTeam: builder.mutation<
      void,
      {
        userId: number;
        type: 'TeamMember' | 'Raider';
      }
    >({
      query: ({ userId, type }) => ({
        url: `/contestants/${userId}`,
        method: 'PATCH',
        body: {
          type: type,
        },
      }),
      invalidatesTags: ['Accounts'],
    }),
    updateCategory: builder.mutation<
      void,
      {
        userId: number;
        type: 'Tribe' | 'Member';
      }
    >({
      query: ({ userId, type }) => ({
        url: `/users/${userId}`,
        method: 'PATCH',
        body: {
          type: type,
        },
      }),
      invalidatesTags: ['Accounts'],
    }),
  }),
});

export const {
  useGetAccountsQuery,
  useGetContestantQuery,
  useGetCountriesQuery,
  useGetAccountByIdQuery,
  useGetContestentByIdQuery,
  useRegisterUserMutation,
  useRegisterContestentMutation,
  useGetUserBankAccountsQuery,
  useGetUserUpiAccountsQuery,
  useGetAccountStatementQuery,
  useUpdateUserStatusMutation,
  useUpdateContestentStatusMutation,
  useUpdateContestentTeamMutation,
  useUpdateContestentAssignTeamMutation,
  useUpdateCategoryMutation,
} = accountsApi;
